<template>
  <div class="flex flex-col space-y-4">
    <lf-dropdown-with-api-search
      v-for="field in fields"
      :key="field.fieldId"
      :placeholder="field.placeholder"
      :name="field.name"
      :value="field.value"
      :get-paginated-options="field.getPaginatedOptions"
      :accessor="field.accessor"
      :show-clear-filter="!!field.value"
      :data-cy="field.dataCy"
      full-width
      @change="selectOption($event, field)"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { TemplateMode } from "@/enums/contractBuilder";
import { useClients } from "@/hooks/clients";
import clientsApiService from "@/services/modules/clients";
import consentTemplateService from "@/services/modules/consentTemplate";
import contractBuilderService from "@/services/modules/contractBuilder";
import widgetTemplatesService from "@/services/modules/widgetTemplates";
import workflowApiService from "@/services/modules/workflows";

import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";

import type { ContractBuilderViewParams } from "@/models/contractBuilder";
import type { WorkflowsViewFilters } from "@/models/workflows";

const { activeClient } = useClients();
const { t } = useI18n();

const customMeta = {
  page: 1,
  total_pages: 1,
  last_page: 1,
  current_page: 1,
  from: 1,
};

const getContractTemplates = async (params: {
  page: number;
  search: string;
}) => {
  const payload: ContractBuilderViewParams = {
    status: TemplateMode.published,
    clients: [activeClient.value!.id],
    ...params,
  };
  return await contractBuilderService.getContractTemplates(payload);
};

const getWidgetBrandingTemplates = async () => {
  const data = await widgetTemplatesService.getWidgetTemplates(
    activeClient.value!.id
  );
  return {
    data,
    meta: {
      ...customMeta,
      total: data.length,
      to: data.length,
    },
  };
};

const getEmailTemplates = async () => {
  const data = await clientsApiService.listEmailCustomisationTemplates(
    activeClient.value!.id
  );

  return {
    data,
    meta: {
      ...customMeta,
      total: data.length,
      to: data.length,
    },
  };
};

const getWorkflowTemplates = async (params: {
  page: number;
  search: string | null;
}) => {
  const payload: WorkflowsViewFilters = {
    active: "1",
    approved: "1",
    ...params,
    search: params.search || "",
  };
  return await workflowApiService.getWorkflowTemplates(payload);
};

const fields = ref([
  {
    fieldId: "workflowTemplateId=",
    placeholder: t("COMMON.WORKFLOW_BUILDER_TEMPLATE"),
    name: "workflow_template",
    dataCy: "widget-token-workflow-template",
    value: "",
    getPaginatedOptions: getWorkflowTemplates,
    accessor: "name",
  },
  {
    fieldId: "brandingId=",
    placeholder: `${t("COMMON.WIDGET")} ${t("COMMON.BRANDING")}`,
    name: "branding",
    dataCy: "widget-token-branding",
    value: "",
    getPaginatedOptions: getWidgetBrandingTemplates,
    accessor: "name",
  },
  {
    fieldId: "emailTemplateId=",
    placeholder: `${t("COMMON.EMAIL")} ${t("COMMON.BRANDING")}`,
    name: "email_template",
    dataCy: "widget-token-email-template",
    value: "",
    getPaginatedOptions: getEmailTemplates,
    accessor: "template_name",
  },
  {
    fieldId: "consentTemplateId=",
    placeholder: t("COMMON.CONSENT_TEMPLATE"),
    name: "consent_template",
    dataCy: "widget-token-consent-template",
    value: "",
    getPaginatedOptions: consentTemplateService.getAllTemplates,
    accessor: "name",
  },
  {
    fieldId: "contractTemplateId=",
    placeholder: t("CONTRACTS.CONTRACT_TEMPLATE"),
    name: "contract_template",
    dataCy: "widget-token-contract-template",
    value: "",
    getPaginatedOptions: getContractTemplates,
    accessor: "name",
  },
]);

const emit = defineEmits<{
  "value:change": [value: { id: string; value: string }[]];
}>();

const selectOption = (
  option: { id: string | number } | null,
  { fieldId }: { fieldId: string }
) => {
  const updatedField = fields.value.find((f) => f.fieldId === fieldId);

  if (!updatedField) {
    return;
  }

  updatedField.value = option?.id.toString() || "";

  const updatedValues = fields.value.reduce(
    (res: { id: string; value: string }[], { fieldId, value }) => {
      res.push({ id: fieldId, value });
      return res;
    },
    []
  );
  emit("value:change", updatedValues);
};
</script>
